<script setup>
import { ref, computed } from 'vue'
import axios from '../axios.js'
import { useUserStore } from '../stores/userStore'

const emit = defineEmits(['success', 'close'])
const userStore = useUserStore()

const name = ref('')
const email = ref('')
const password = ref('')
const showPassword = ref(false)
const role = ref('client') // Default to client role
const status = ref('active') // Default status
const customPermissions = ref([])
const useRoleDefaults = ref(true) // Whether to use role default permissions or custom
const loading = ref(false)
const error = ref('')

// Available roles from the store
const availableRoles = userStore.roles

// Available permissions organized by category
const permissionCategories = {
  'Core System': {
    'view_fixtures': 'View Fixtures',
    'edit_fixtures': 'Edit Fixtures',
    'delete_fixtures': 'Delete Fixtures',
    'view_odds': 'View Odds',
    'edit_odds': 'Edit Odds',
    'manage_risk': 'Manage Risk'
  },
  'Data Management': {
    'view_reports': 'View Reports',
    'edit_reports': 'Edit Reports',
    'view_analytics': 'View Analytics',
    'view_monitoring': 'View Monitoring',
    'edit_monitoring': 'Edit Monitoring'
  },
  'Administrative': {
    'view_users': 'View Users',
    'edit_users': 'Edit Users',
    'delete_users': 'Delete Users',
    'manage_server': 'Manage Server',
    'manage_database': 'Manage Database',
    'view_tickets': 'View Tickets',
    'edit_tickets': 'Edit Tickets',
    'delete_tickets': 'Delete Tickets',
    'manage_templates': 'Manage Templates'
  },
  'Navigation & Menu Access': {
    'view_games': 'Access Sports Menu',
    'view_templates': 'Access Templates',
    'view_users_menu': 'Access Users Menu',
    'view_tickets_menu': 'Access Tickets Menu',
    'view_monitoring': 'Access Monitoring',
    'view_server_manager': 'Access Server Manager',
    'view_settings': 'Access Settings'
  },
  'Special Access': {
    '*': 'Full Access (All Permissions)'
  }
}

const selectedRolePermissions = computed(() => {
  const selectedRole = availableRoles.find(r => r.id === role.value)
  return selectedRole ? selectedRole.permissions : []
})

const finalPermissions = computed(() => {
  return useRoleDefaults.value ? selectedRolePermissions.value : customPermissions.value
})

const togglePermission = (permission) => {
  const index = customPermissions.value.indexOf(permission)
  if (index > -1) {
    customPermissions.value.splice(index, 1)
  } else {
    customPermissions.value.push(permission)
  }
}

const hasPermission = (permission) => {
  return customPermissions.value.includes(permission)
}

const formatPermission = (permission) => {
  if (permission === '*') return 'Full Access'
  return permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

const setRoleDefaults = () => {
  customPermissions.value = [...selectedRolePermissions.value]
  useRoleDefaults.value = false
}

const grantAllPermissions = () => {
  customPermissions.value = ['*']
  useRoleDefaults.value = false
}

const generateHackeryfyPassword = () => {
  if (!email.value) {
    error.value = 'Please enter an email first'
    return
  }
  
  // Generate password based on email using a deterministic algorithm
  const emailStr = email.value.toLowerCase()
  let hash = 0
  for (let i = 0; i < emailStr.length; i++) {
    const char = emailStr.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32bit integer
  }
  
  // Create a password using the hash and email parts
  const emailParts = emailStr.split('@')
  const username = emailParts[0] || ''
  const domain = emailParts[1]?.split('.')[0] || ''
  
  // Generate password: mix of hash, username, and domain with transformations
  const hashStr = Math.abs(hash).toString(36).substring(0, 6)
  const usernamePart = username.substring(0, 3).padEnd(3, 'x')
  const domainPart = domain.substring(0, 2).padEnd(2, 'y')
  
  // Combine with special characters and numbers
  const specialChars = '!@#$%^&*'
  const specialChar = specialChars[Math.abs(hash) % specialChars.length]
  const number = Math.abs(hash) % 100
  
  password.value = `${usernamePart}${hashStr}${domainPart}${number}${specialChar}`
}

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    // You could add a toast notification here if needed
  } catch (err) {
    console.error('Failed to copy text: ', err)
    error.value = 'Failed to copy to clipboard'
  }
}

const copyEmail = () => {
  if (email.value) {
    copyToClipboard(email.value)
  }
}

const copyPassword = () => {
  if (password.value) {
    copyToClipboard(password.value)
  }
}

const copyBoth = () => {
  if (email.value && password.value) {
    copyToClipboard(`${email.value} ${password.value}`)
  }
}

const submit = async () => {
  error.value = ''
  loading.value = true
  try {
    await axios.post('/api/create-user', {
      name: name.value,
      email: email.value,
      password: password.value,
      role: role.value,
      status: status.value,
      permissions: finalPermissions.value
    })
    emit('success')
  } catch (err) {
    error.value = err.response?.data?.message || err.response?.data?.error || 'Failed to create user'
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="fixed inset-0 bg-black/40 flex items-center justify-center z-50 overflow-y-auto" @click.self="emit('close')">
    <div class="bg-white rounded-xl shadow-xl w-full max-w-4xl p-8 relative my-8 mx-4">
      <button class="absolute top-4 right-4 text-gray-400 hover:text-gray-600" @click="emit('close')">
        <i class="fas fa-times"></i>
      </button>
      <h2 class="text-xl font-semibold mb-6">Add New User</h2>
      
      <form @submit.prevent="submit" class="space-y-6">
        <!-- Basic Information -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium mb-1">Name</label>
          <input v-model="name" type="text" class="w-full border rounded-lg px-3 py-2" required />
        </div>
        <div>
          <label class="block text-sm font-medium mb-1">Email</label>
          <div class="relative">
            <input v-model="email" type="email" class="w-full border rounded-lg px-3 py-2 pr-10" required />
            <button
              type="button"
              @click="copyEmail"
              class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
              :disabled="!email"
              tabindex="-1"
              title="Copy email"
            >
              <i class="fas fa-copy"></i>
            </button>
          </div>
        </div>
        
        <div>
          <label class="block text-sm font-medium mb-1">Password</label>
          <div class="flex gap-2">
            <div class="flex-1 relative">
              <input 
                v-model="password" 
                :type="showPassword ? 'text' : 'password'" 
                class="w-full border rounded-lg px-3 py-2 pr-20" 
                required 
              />
              <div class="absolute right-3 top-1/2 transform -translate-y-1/2 flex gap-2">
                <button
                  type="button"
                  @click="copyPassword"
                  class="text-gray-500 hover:text-gray-700 focus:outline-none"
                  :disabled="!password"
                  tabindex="-1"
                  title="Copy password"
                >
                  <i class="fas fa-copy"></i>
                </button>
                <button
                  type="button"
                  @click="togglePasswordVisibility"
                  class="text-gray-500 hover:text-gray-700 focus:outline-none"
                  tabindex="-1"
                  title="Toggle password visibility"
                >
                  <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </button>
              </div>
            </div>
            <button
              type="button"
              @click="generateHackeryfyPassword"
              class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium whitespace-nowrap"
              :disabled="!email"
            >
              Generate Password
            </button>
          </div>
        </div>
        <div>
          <button
            type="button"
            @click="copyBoth"
            class="w-full px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium"
            :disabled="!email || !password"
          >
            <i class="fas fa-copy mr-2"></i>
            Copy Email and Password (separated by space)
          </button>
        </div>
          <div>
            <label class="block text-sm font-medium mb-1">Status</label>
            <select v-model="status" class="w-full border rounded-lg px-3 py-2" required>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>
        
        <!-- Role Selection -->
        <div>
          <label class="block text-sm font-medium mb-1">Base Role</label>
          <select v-model="role" class="w-full border rounded-lg px-3 py-2" required>
            <option v-for="roleOption in availableRoles" :key="roleOption.id" :value="roleOption.id">
              {{ roleOption.name }}
            </option>
          </select>
          <div class="text-xs text-gray-500 mt-1">
            Default permissions: {{ selectedRolePermissions.map(formatPermission).join(', ') }}
          </div>
        </div>

        <!-- Permission Selection -->
        <div class="border-t pt-6">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-medium">Permissions</h3>
            <div class="flex gap-2">
              <button
                type="button"
                @click="useRoleDefaults = true"
                class="px-3 py-1 text-sm rounded transition-colors"
                :class="useRoleDefaults ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              >
                Use Role Defaults
              </button>
              <button
                type="button"
                @click="setRoleDefaults"
                class="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                Customize from Role
              </button>
              <button
                type="button"
                @click="grantAllPermissions"
                class="px-3 py-1 text-sm bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-colors"
              >
                Grant All
              </button>
            </div>
          </div>

          <!-- Show role defaults or custom permission selector -->
          <div v-if="useRoleDefaults" class="p-4 bg-blue-50 rounded-lg">
            <div class="text-sm text-blue-800 mb-2">Using default permissions for {{ role }} role:</div>
            <div class="flex flex-wrap gap-2">
              <span 
                v-for="permission in selectedRolePermissions" 
                :key="permission"
                class="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs"
              >
                {{ formatPermission(permission) }}
              </span>
            </div>
          </div>

          <div v-else class="space-y-6">
            <!-- Custom Permission Categories -->
            <div v-for="(permissions, category) in permissionCategories" :key="category">
              <h4 class="text-sm font-medium text-gray-700 mb-3">{{ category }}</h4>
              <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div 
                  v-for="(label, permission) in permissions" 
                  :key="permission"
                  class="flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all hover:shadow-sm"
                  :class="{
                    'border-green-500 bg-green-50': hasPermission(permission),
                    'border-yellow-500 bg-yellow-50': permission === '*' && !hasPermission(permission),
                    'border-gray-200 hover:border-gray-300': permission !== '*' && !hasPermission(permission)
                  }"
                  @click="togglePermission(permission)"
                >
                  <div 
                    class="w-5 h-5 rounded border-2 flex items-center justify-center transition-colors"
                    :class="{
                      'border-green-500 bg-green-500': hasPermission(permission) && permission !== '*',
                      'border-yellow-500 bg-yellow-500': hasPermission(permission) && permission === '*',
                      'border-yellow-400': !hasPermission(permission) && permission === '*',
                      'border-gray-300': !hasPermission(permission) && permission !== '*'
                    }"
                  >
                    <i v-if="hasPermission(permission)" :class="permission === '*' ? 'fas fa-crown text-white text-xs' : 'fas fa-check text-white text-xs'"></i>
                  </div>
                  <div>
                    <div class="text-sm font-medium">{{ label }}</div>
                    <div class="text-xs text-gray-500">{{ permission }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Permission Summary -->
            <div class="p-4 bg-gray-50 rounded-lg">
              <h5 class="text-sm font-medium text-gray-700 mb-2">Selected Permissions Summary</h5>
              <div class="flex flex-wrap gap-2">
                <span 
                  v-for="permission in customPermissions" 
                  :key="permission"
                  class="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs"
                >
                  {{ formatPermission(permission) }}
                </span>
                <span v-if="customPermissions.length === 0" class="text-sm text-gray-500">
                  No permissions selected
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div v-if="error" class="text-red-600 text-sm">{{ error }}</div>
        <button type="submit" class="w-full bg-blue-600 text-white rounded-lg py-2 font-medium hover:bg-blue-700" :disabled="loading">
          <span v-if="loading" class="animate-spin mr-2"><i class="fas fa-spinner"></i></span>
          Add User
        </button>
      </form>
    </div>
  </div>
</template> 